import { isMobile } from "react-device-detect";
import { CarModelCameraList } from "../ModelLoad/ModelLoadController";
import { ConfiguratorModel } from "../ConfiguratorModel";
import { HotspotController } from "../HotspotController";
import { SequenceEntry, SequenceInterpreter } from "./SequencePlayer";

export class DefaultSequenceInterpreter implements SequenceInterpreter {
  private _originalLightOption:number;
  private _originalCarPaintOption:number;
  private _originalEnvOption:number;
  private _didHideHotspots:boolean;
  private _curtain:HTMLDivElement;
  private _curtainParent:HTMLDivElement;

  public constructor(
    private model:ConfiguratorModel,
    private hotspotController:HotspotController,
    private setTextHandler:(text:string, timeout:number, fade:boolean, position:string)=>void = () => {}
  ) {}

  public async onStart():Promise<void> {
    this._originalLightOption = this.model.lightsSelectionGroup.currentSelection;
    this._originalCarPaintOption = this.model.carpaintSelectionGroup.currentSelection;
    this._originalEnvOption = this.model.environmentGroup.currentSelection;
    
    if (this.model.isLightOn()) {
      this.model.turnLight();
    }

    if (this.model.controller.carOpen) {
      await this.model.controller.playAnimation();
    }

    this._didHideHotspots = this.hotspotController.visible;
    this.hotspotController.hideHotspots();

    // Move the curtain element on top of everything else temporarily
    const curtain = document.getElementById("curtain");
    if (!curtain) {
      throw 'Cannot find thengine curtain DIV';
    }
    this._curtainParent = curtain.parentNode;
    this._curtain = curtain;
    this._curtainParent.removeChild(curtain);
    curtain.style.top = "0";
    document.body.appendChild(curtain);
  }

  public onEnd():void {
    const lightsSelectionGroup = this.model.lightsSelectionGroup;
    const carpaintSelectionGroup = this.model.carpaintSelectionGroup;
    const envSelectionGroup = this.model.environmentGroup;

    this.model.changeSelection(lightsSelectionGroup.groupID, lightsSelectionGroup.selections[this._originalLightOption].id);
    this.model.changeSelection(carpaintSelectionGroup.groupID, carpaintSelectionGroup.selections[this._originalCarPaintOption].id);
    this.model.changeSelection(envSelectionGroup.groupID, envSelectionGroup.selections[this._originalEnvOption].id);
    this.model.controller.setCamera(CarModelCameraList.Orbit);
    this.setTextHandler("", 0, false, "bottom"); // remove me?

    if (this.model.controller.carOpen) {
      this.model.controller.playAnimation();
    }

    // hide presentation hotspots in case we opened them.
    this.hotspotController.hideHotspots();

    if (this._didHideHotspots) {
      this.hotspotController.showHotspots();
    }

    // Add curtain element back to its parent.
    this._curtainParent.appendChild(this._curtain);
  }

  public async do(entry:SequenceEntry):Promise<void> {
    const controller3D = this.model.controller;
    const renderService = controller3D.engine.engine.services.render;

    for (const cmd of entry.cmds) {
      if (cmd.type == "set_camera") {
        if (cmd.data.hasOwnProperty("key")) {
          this.model.controller.setCamera(cmd.data.key, cmd.data.time, cmd.data.easing);
        } else {
          let name = cmd.data.desktop;
          if (isMobile && cmd.data.hasOwnProperty("mobile")) {
            name = cmd.data.mobile;
          }
          this.model.controller.setCameraDirect(name, cmd.data.time, cmd.data.easing);
        }
      } else if (cmd.type == "show_text") {
        let text = cmd.data.text;
        text = text.replaceAll('$model', this.model.modelLoadController.getModelName());
        this.setTextHandler(text, cmd.data.timeout || entry.duration, cmd.data.fade || false, cmd.data.position || "bottom");
      } else if (cmd.type == "light_on") {
        // TODO: restore original state
        if (!this.model.isLightOn()) { 
          await this.model.turnLight();
        }
      } else if (cmd.type == "light_off") {
        // TODO: restore original state
        if (this.model.isLightOn()) {
          await this.model.turnLight();
        }
      } else if (cmd.type == "carpaint_next") {
        // TODO: restore original state
        const selectionGroup = this.model.carpaintSelectionGroup;
        const nextIndex = (selectionGroup.currentSelection + 1) % selectionGroup.selections.length;
        await this.model.changeSelection(selectionGroup.groupID, selectionGroup.selections[nextIndex].id);
      } else if (cmd.type == "env_next") {
        const selectionGroup = this.model.environmentGroup;
        const nextIndex = (selectionGroup.currentSelection + 1) % selectionGroup.selections.length;
        await this.model.changeSelection(selectionGroup.groupID, selectionGroup.selections[nextIndex].id);
      } else if (cmd.type == "open") {
        // TODO: how to handle if the door is already open?
        // TODO: restore original state?
        for (const thing of cmd.data) {
          switch (thing) {
            case "fl":    if (!controller3D.doorFrontLeftOpen)  controller3D.playFrontLeftDoorAnimation();  break;
            case "fr":    if (!controller3D.doorFrontRightOpen) controller3D.playFrontRightDoorAnimation(); break;
            case "rl":    if (!controller3D.doorRearLeftOpen)   controller3D.playRearLeftDoorAnimation();   break;
            case "rr":    if (!controller3D.doorRearRightOpen)  controller3D.playRearRightDoorAnimation();  break;
            case "trunk": if (!controller3D.trunkLidOpen)       controller3D.playTrunkLidAnimation();       break;
          }
        }
      } else if (cmd.type == "close") {
        // TODO: how to handle if the door is already closed?
        // TODO: restore original state?
        for (const thing of cmd.data) {
          switch (thing) {
            case "fl":    if (controller3D.doorFrontLeftOpen)  controller3D.playFrontLeftDoorAnimation();  break;
            case "fr":    if (controller3D.doorFrontRightOpen) controller3D.playFrontRightDoorAnimation(); break;
            case "rl":    if (controller3D.doorRearLeftOpen)   controller3D.playRearLeftDoorAnimation();   break;
            case "rr":    if (controller3D.doorRearRightOpen)  controller3D.playRearRightDoorAnimation();  break;
            case "trunk": if (controller3D.trunkLidOpen)       controller3D.playTrunkLidAnimation();       break;
          }
        }
      } else if (cmd.type == "show_hotspots") {
        this.hotspotController.showPresentationHotspots();
      } else if (cmd.type == "hide_hotspots") {
        this.hotspotController.hideHotspots();
      } else if (cmd.type == "show_hotspot_info") {
        this.hotspotController.openHotspot(cmd.data);
      } else if (cmd.type == "hide_hotspot_info") {
        this.hotspotController.closeHotspot();
      } else if (cmd.type == "fade_in") {
        renderService.fadeIn(cmd.data.time, cmd.data.color);
        this._curtain.style.zIndex = "2";
      } else if (cmd.type == "fade_out") {
        renderService.fadeOut(cmd.data.time, cmd.data.color);
        this._curtain.style.zIndex = "2";
      } else {
        console.warn("DefaultSequenceInterpreter: unhandled command:", cmd.type);
      }
    }
  }
}